/**
 * Встроенный AI-чат для карточки записи.
 * Использует скоуп { kind: 'item', itemId } — Lensy видит конкретную запись.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Input, Typography, Empty, Tooltip, Switch, Space } from 'antd';
import {
  SendOutlined,
  CloseOutlined,
  LoadingOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  PlusOutlined,
  ToolOutlined,
  CopyOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { AiSparkIcon } from '../../shared/components/AiSparkIcon.tsx';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { AiChatRequest } from '@lens/shared';
import { streamChat, type SseCallbacks } from './api.ts';
import { message as messageApi } from 'antd';
import './ai-markdown.css';

const { Text } = Typography;
const { TextArea } = Input;

interface ToolCallInfo {
  toolCallId: string;
  name: string;
  args: Record<string, unknown>;
}

interface ToolResultInfo {
  toolCallId: string;
  name: string;
  success: boolean;
  summary: string;
}

type MessagePart =
  | { type: 'text'; content: string }
  | { type: 'tool_call'; info: ToolCallInfo }
  | { type: 'tool_result'; info: ToolResultInfo };

interface Message {
  role: 'user' | 'assistant';
  parts: MessagePart[];
}

const TOOL_NAMES_RU: Record<string, string> = {
  get_item: 'Загрузка записи',
  query_items: 'Поиск записей',
  update_items: 'Обновление записей',
  add_comment: 'Добавление комментария',
  list_comments: 'Загрузка комментариев',
  get_audit_log: 'История изменений',
};

interface ItemAiChatProps {
  itemId: string;
}

export function ItemAiChat({ itemId }: ItemAiChatProps): JSX.Element {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [enableTools, setEnableTools] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const stickyBottomRef = useRef(true);

  // Сброс при смене записи
  useEffect(() => {
    setMessages([]);
    setInput('');
    setStreaming(false);
    abortRef.current?.abort();
    abortRef.current = null;
    stickyBottomRef.current = true;
  }, [itemId]);

  useEffect(() => {
    if (!stickyBottomRef.current) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    stickyBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  }, []);

  const appendToAssistant = useCallback((part: MessagePart) => {
    setMessages((prev) => {
      const copy = [...prev];
      const last = copy[copy.length - 1];
      if (last?.role === 'assistant') {
        const lastPart = last.parts[last.parts.length - 1];
        if (part.type === 'text' && lastPart?.type === 'text') {
          copy[copy.length - 1] = {
            ...last,
            parts: [
              ...last.parts.slice(0, -1),
              { type: 'text', content: lastPart.content + part.content },
            ],
          };
        } else {
          copy[copy.length - 1] = { ...last, parts: [...last.parts, part] };
        }
      }
      return copy;
    });
  }, []);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || streaming) return;
      stickyBottomRef.current = true;

      const MAX_HISTORY_CONTENT = 20000;
      const history = messages
        .slice(-20)
        .map((msg) => ({
          role: msg.role as 'user' | 'assistant',
          content: msg.parts
            .filter((p) => p.type === 'text')
            .map((p) => (p as { type: 'text'; content: string }).content)
            .join('')
            .slice(0, MAX_HISTORY_CONTENT),
        }))
        .filter((h) => h.content.length > 0);

      setMessages((prev) => [...prev, { role: 'user', parts: [{ type: 'text', content: text.trim() }] }]);
      setInput('');
      setStreaming(true);
      setMessages((prev) => [...prev, { role: 'assistant', parts: [] }]);

      const controller = new AbortController();
      abortRef.current = controller;

      const request: AiChatRequest = {
        scope: { kind: 'item', itemId },
        message: text.trim(),
        ...(enableTools ? { enableTools: true } : {}),
        ...(history.length > 0 ? { history } : {}),
      };

      const callbacks: SseCallbacks = {
        onDelta: (chunk) => appendToAssistant({ type: 'text', content: chunk }),
        onToolCall: (toolCallId, name, args) =>
          appendToAssistant({ type: 'tool_call', info: { toolCallId, name, args } }),
        onToolResult: (toolCallId, name, success, summary) =>
          appendToAssistant({ type: 'tool_result', info: { toolCallId, name, success, summary } }),
        onDone: () => {
          setStreaming(false);
          abortRef.current = null;
        },
        onError: (_code, msg) => {
          appendToAssistant({ type: 'text', content: `**Ошибка:** ${msg ?? 'Не удалось получить ответ'}` });
          setStreaming(false);
          abortRef.current = null;
        },
      };

      try {
        await streamChat(request, callbacks, controller.signal);
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          appendToAssistant({ type: 'text', content: '**Ошибка:** Не удалось получить ответ от Lensy' });
        }
        setStreaming(false);
        abortRef.current = null;
      }
    },
    [itemId, streaming, messages, appendToAssistant],
  );

  const handleStop = useCallback(() => {
    abortRef.current?.abort();
    setStreaming(false);
  }, []);

  const handleNewChat = useCallback(() => {
    abortRef.current?.abort();
    setStreaming(false);
    setMessages([]);
    setInput('');
    stickyBottomRef.current = true;
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void sendMessage(input);
    }
  };

  const handleCopy = useCallback((text: string) => {
    void navigator.clipboard.writeText(text).then(
      () => { void messageApi.success('Скопировано', 1); },
      () => { void messageApi.error('Не удалось скопировать'); },
    );
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 420 }}>
      {/* Заголовок */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 0 10px',
          borderBottom: '1px solid #f0f0f0',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <AiSparkIcon
            style={{ fontSize: 16, color: '#1677ff' }}
            className={streaming ? 'lensy-icon-pulse' : ''}
          />
          <Text strong style={{ fontSize: 13 }}>Lensy</Text>
          {streaming && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              <LoadingOutlined style={{ marginRight: 4 }} />
              думает…
            </Text>
          )}
        </div>
        <Space size={8}>
          <Tooltip title="Разрешить Lensy обновлять эту запись и добавлять комментарии">
            <Space size={4}>
              <ThunderboltOutlined style={{ color: enableTools ? '#faad14' : '#d9d9d9', fontSize: 13 }} />
              <Switch size="small" checked={enableTools} onChange={setEnableTools} />
              <Text type="secondary" style={{ fontSize: 12 }}>Действия</Text>
            </Space>
          </Tooltip>
          {messages.length > 0 && (
            <Tooltip title="Новый чат">
              <Button size="small" type="text" icon={<PlusOutlined />} onClick={handleNewChat} />
            </Tooltip>
          )}
        </Space>
      </div>

      {/* Сообщения */}
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        style={{ flex: 1, overflow: 'auto', padding: '12px 0' }}
      >
        {messages.length === 0 && (
          <Empty
            description="Задайте вопрос по этой записи"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ marginTop: 24 }}
          />
        )}

        {streaming && messages.length > 0 && messages[messages.length - 1]?.role === 'user' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, color: '#8c8c8c', fontSize: 13 }}>
            <span className="lensy-thinking-dots" aria-hidden="true">
              <span /><span /><span />
            </span>
            <span>Lensy думает…</span>
          </div>
        )}

        {messages.map((msg, i) => {
          const isLast = i === messages.length - 1;
          const assistantText =
            msg.role === 'assistant'
              ? msg.parts
                  .filter((p) => p.type === 'text')
                  .map((p) => (p as { type: 'text'; content: string }).content)
                  .join('')
              : '';
          return (
            <div
              key={i}
              style={{
                marginBottom: 12,
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              }}
            >
              <div style={{ maxWidth: '90%', wordBreak: 'break-word', fontSize: 13 }}>
                {msg.role === 'user' ? (
                  <div
                    style={{
                      padding: '6px 12px',
                      borderRadius: '14px 14px 4px 14px',
                      background: '#e6f4ff',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {assistantText ||
                      msg.parts
                        .filter((p) => p.type === 'text')
                        .map((p) => (p as { type: 'text'; content: string }).content)
                        .join('')}
                  </div>
                ) : (
                  <div>
                    {msg.parts.map((part, j) => renderPart(part, j, TOOL_NAMES_RU, streaming && isLast && j === msg.parts.length - 1))}
                    {streaming && isLast && msg.parts.length === 0 && <LoadingOutlined />}
                    {!streaming && isLast && assistantText && (
                      <Tooltip title="Скопировать">
                        <Button
                          type="text"
                          size="small"
                          icon={<CopyOutlined />}
                          onClick={() => handleCopy(assistantText)}
                          style={{ marginTop: 2, marginLeft: 4 }}
                        />
                      </Tooltip>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Ввод */}
      <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 10, flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <TextArea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={enableTools ? 'Опишите действие или задайте вопрос…' : 'Задайте вопрос по записи…'}
            autoSize={{ minRows: 1, maxRows: 4 }}
            disabled={streaming}
            style={{ resize: 'none', flex: 1, borderRadius: 8, fontSize: 13 }}
          />
          {streaming ? (
            <Button
              icon={<CloseOutlined />}
              onClick={handleStop}
              danger
              style={{ flexShrink: 0, borderRadius: 8 }}
              title="Остановить"
            />
          ) : (
            <Button
              icon={<SendOutlined />}
              onClick={() => void sendMessage(input)}
              type="primary"
              disabled={!input.trim()}
              style={{ flexShrink: 0, borderRadius: 8 }}
              title="Отправить (Enter)"
            />
          )}
        </div>
        <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
          Enter — отправить · Shift+Enter — перенос строки
        </Text>
      </div>
    </div>
  );
}

function renderPart(
  part: MessagePart,
  index: number,
  toolNamesRu: Record<string, string>,
  isLast: boolean,
): JSX.Element | null {
  if (part.type === 'text') {
    return (
      <div
        key={index}
        style={{
          padding: '8px 12px',
          borderRadius: '14px 14px 14px 4px',
          background: '#f5f5f5',
        }}
      >
        <div className="ai-markdown">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{part.content}</ReactMarkdown>
          {isLast && <LoadingOutlined style={{ marginLeft: 4 }} />}
        </div>
      </div>
    );
  }

  if (part.type === 'tool_call') {
    const label = toolNamesRu[part.info.name] || part.info.name;
    return (
      <div
        key={index}
        style={{
          margin: '4px 0',
          padding: '6px 10px',
          borderRadius: 8,
          background: '#f0f5ff',
          border: '1px solid #adc6ff',
          fontSize: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <ToolOutlined style={{ color: '#1677ff', fontSize: 12 }} />
        <Text style={{ fontSize: 12 }}>{label}</Text>
      </div>
    );
  }

  if (part.type === 'tool_result') {
    const { success, summary } = part.info;
    return (
      <div
        key={index}
        style={{
          margin: '2px 0 6px',
          padding: '4px 10px',
          borderRadius: 8,
          background: success ? '#f6ffed' : '#fff2f0',
          border: `1px solid ${success ? '#b7eb8f' : '#ffa39e'}`,
          fontSize: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        {success ? (
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 12 }} />
        ) : (
          <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 12 }} />
        )}
        <Text style={{ fontSize: 12 }}>{summary}</Text>
      </div>
    );
  }

  return null;
}
